import {
  DeleteFilled,
  EditFilled,
  QuestionCircleOutlined
} from '@ant-design/icons';
import { DragSortingTable } from '../DraggableTable/DraggingTable';
import {
  Button,
  Card,
  Divider,
  Form,
  Image,
  Input,
  Modal,
  Table,
  Select,
  Tag,
  Popconfirm
} from 'antd';
import { observer } from 'mobx-react';
import React, { useContext, useState, useEffect } from 'react';
import { rootContext } from 'stores';
import './EditTrims.scss';

export const EditTrims: React.FC = observer(() => {
  const [file, setFile] = useState<File | null>(null);

  const [addTrims] = Form.useForm();

  const {
    PartsStore: {
      state: { data, ui },
      setState,
      deleteTrim,
      createTrim,
      updateTrim,
      orderTrim
    },
    RegionStore: {
      getRegions,
      state: {
        data: { regions },
        ui: { getRegionsLoading }
      }
    }
  } = useContext(rootContext);
  useEffect(() => {
    getRegions();
  }, [getRegions]);

  const validations = (rule: any, value: any) => {
    if (value && value?.trim()?.length === 0) {
      return Promise.reject('Whitespaces are not valid.');
    } else {
      return Promise.resolve(value?.trim() || '');
    }
  };

  return (
    <div className="SubmodelsStyles">
      <Modal
        title="Trims"
        width="80%"
        className="Submodel__modal__box"
        visible={Boolean(data.editTrim)}
        footer={false}
        destroyOnClose={true}
        onCancel={() => {
          setState({
            data: {
              ...data,
              editTrim: null,
              selectedTrim: null,
              trims: [],
              selectedSubmodel: null
            }
          });
          addTrims.resetFields();
        }}
      >
        <Form
          className="trimsFrom"
          layout="inline"
          form={addTrims}
          onFinish={async values => {
            if (!data.selectedTrim && data.editTrim && file) {
              const response = await createTrim({
                file: file,
                name: values.name,
                region: values.region,
                submodel_id: data.editTrim
              });
              if (response) {
                setFile(null);
                addTrims.resetFields();
              }
            } else if (!data.selectedTrim && data.editTrim) {
              const response = await createTrim({
                name: values.name,
                region: values.region,
                submodel_id: data.editTrim
              });
              if (response) {
                setFile(null);
                addTrims.resetFields();
              }
            } else if (data.editTrim && data.selectedTrim) {
              const response = await updateTrim({
                ...(file ? { file: file } : {}),
                id: data.selectedTrim.id,
                name: values.name,
                region: values.region
              });
              if (response) {
                setFile(null);
                addTrims.resetFields();
              }
            }
          }}
          onSubmitCapture={e => {}}
        >
          <Form.Item
            label="Trim"
            name="name"
            rules={[
              { required: true, message: 'Trim name is required' },
              { validator: validations }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Region"
            name="region"
            rules={[
              {
                required: Boolean(!data.selectedTrim),
                message: 'Region is required'
              }
            ]}
          >
            <Select
              tagRender={({ value }) => {
                return <Tag>{regions.find(rg => rg.id === value)?.name}</Tag>;
              }}
              style={{ width: '400px' }}
              loading={getRegionsLoading}
              placeholder="Select Region"
              mode="multiple"
              showSearch={false}
              dropdownRender={menu => (
                <div>
                  <Divider style={{ margin: '4px 0' }} />
                  {menu}
                </div>
              )}
            >
              {regions?.map(({ name, id }) => (
                <Select.Option key={id} value={id} className="regionOption">
                  <span>{name}</span>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Image"
            tooltip={
              data.selectedSubmodel?.url
                ? `Image will inherit submodel level image unless passed`
                : `Image is required since submodel has no image specified`
            }
            name="file"
            rules={[
              {
                required: Boolean(!data.selectedSubmodel?.url),
                message: 'Image is required'
              }
            ]}
          >
            <Input
              onChange={e => e.target.files && setFile(e.target.files[0])}
              type="file"
              accept=".jpg,.png,.jpeg,.gif"
              className="upload__file"
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={ui.addTrimLoading}
            >
              {data.selectedTrim ? 'Edit Trim' : 'Add Trim'}
            </Button>
          </Form.Item>
          {data.selectedTrim && (
            <Button
              type="primary"
              disabled={ui.addSubmodelLoading}
              onClick={() => {
                setState({
                  data: { ...data, selectedTrim: null }
                });
                addTrims.resetFields();
              }}
            >
              Cancel Editing
            </Button>
          )}
        </Form>
        <Divider />
        <Card>
          <DragSortingTable
            rowKey="id"
            draggedRow={(index: number, id: string) =>
              orderTrim({ index, id, type: 'TRIM' })
            }
            className="subModelTable"
            dataSource={data.trims}
            pagination={false}
            loading={Boolean(
              ui.deleteTrimLoading || ui.addTrimLoading || ui.editTrimLoading
            )}
            columns={[
              {
                dataIndex: 'name',
                title: 'Name',
                render: (name, row) => {
                  let selectedStyle = {
                    ...(row?.id === data?.selectedSubmodel?.id
                      ? {
                          colour: 'red',
                          fontWeight: 'bold'
                        }
                      : {})
                  };
                  // @ts-ignore
                  return <p style={selectedStyle}>{name}</p>;
                }
              },
              {
                title: 'Image',
                dataIndex: 'image',
                align: 'center',
                width: 10,
                render: (value, row) => (
                  <div key={row.id} className="tableImageLarge">
                    <Image className="tableImage" src={value} />
                  </div>
                )
              },
              {
                title: 'Regions',
                dataIndex: 'regions',
                align: 'center',
                render: (value, row) => {
                  return !value?.length ? (
                    <Button>Add Trims</Button>
                  ) : (
                    value?.map((item: { name: string }) => (
                      <Tag>{item.name}</Tag>
                    ))
                  );
                }
              },
              {
                title: 'Action',
                width: 10,
                align: 'center',
                render: (_, row) => (
                  <div className="actionButtons">
                    <Button
                      ghost={true}
                      className="editButton"
                      disabled={ui.deleteSubModelLoading === row.id}
                      onClick={() => {
                        setState({ data: { ...data, selectedTrim: row } });
                        addTrims.setFieldsValue({
                          name: row.name,
                          region: row.regions.map(
                            (region: { id: string }) => region.id
                          )
                        });
                      }}
                    >
                      <EditFilled />
                    </Button>
                    <Popconfirm
                      onConfirm={async () => {
                        setState({
                          ui: { ...ui, deleteTrimLoading: row.id }
                        });
                        await deleteTrim(row.id);
                      }}
                      onCancel={e => e?.stopPropagation()}
                      title={() => (
                        <p>
                          Delete this <b>Trim</b> ？
                        </p>
                      )}
                      icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                    >
                      <Button
                        ghost={true}
                        className="deleteButton"
                        loading={ui.deleteSubModelLoading === row.id}
                      >
                        <DeleteFilled />
                      </Button>
                    </Popconfirm>
                  </div>
                )
              }
            ]}
          ></DragSortingTable>
        </Card>
      </Modal>
    </div>
  );
});
